import { Box, alpha } from '@mui/material';
import { motion, useInView } from 'framer-motion';
import { useRef, type ReactNode, type CSSProperties, lazy, Suspense } from 'react';
import { CRM_PALETTE as P } from '../../theme/brandPalette';
import { BOOSTER_BASE } from '../../theme/brand';

// ── Tipografía de la landing ────────────────────────────────────────────────
/**
 * Tres roles, no tres gustos.
 *
 * Orbitron dejó los TITULARES (20/08/2026). A 54px sus letras se abren tanto que el
 * titular pierde peso y suena a videojuego — justo lo contrario de la seriedad que tiene
 * que transmitir la plataforma. Se queda donde rinde: el logotipo (`BoosterMark`) y el
 * login, que es de dónde salió.
 *
 * `FONT_DISPLAY` pasa a Space Grotesk: geometría técnica con detalles raros —la ge, la
 * erre— que dan carácter sin disfraz. `FONT_TECH` pasa a una monoespaciada, que es lo que
 * de verdad hace que las etiquetas y las métricas se lean como un instrumento.
 */
export const FONT_DISPLAY = "'Space Grotesk', 'Inter', sans-serif";        // titulares
export const FONT_TECH = "'JetBrains Mono', ui-monospace, monospace";      // etiquetas/métricas
export const FONT_BODY = "'Inter', system-ui, sans-serif";                 // cuerpo / UI
/** La display de MARCA. Solo logotipo: no vuelve a los titulares. */
export const FONT_BRAND = "'Orbitron', 'Space Grotesk', sans-serif";

// ── Tokens de marca Booster (emerald) ───────────────────────────────────────
// Desde la marca PRISTINA (BOOSTER_BASE), no de CRM_PALETTE: el landing público nunca debe
// tomar el acento white-label del tenant logueado (se capturaban a nivel módulo → se "congelaban").
/**
 * ⚠️ ESTOS NOMBRES MIENTEN Y SE CONSERVAN POR LOS CALLERS.
 *
 * No son verdes desde el 20/08/2026: leen `BOOSTER_BASE.brand`, que es el acento vigente de
 * la marca. Cuando el acento pasó a cian, y de ahí al celeste (25/08/2026), estos siguieron
 * al acento sin que nadie los tocara — los comentarios que decían `#059669` estuvieron cinco
 * días describiendo un color que ya no existía.
 *
 * Queda UN uso real (`EMERALD_LIGHT` como estado "hecho" en GustavoExecutes). Si necesitás
 * un color para decorar, el que va es `ACENTO`.
 */
export const EMERALD = BOOSTER_BASE.brand.golden;            // = ACENTO — solo marca
export const EMERALD_LIGHT = BOOSTER_BASE.brand.goldenLight; // = el claro del acento
export const EMERALD_DEEP = BOOSTER_BASE.brand.goldenDark;   // = el hondo del acento

/**
 * EL ACENTO de la web pública: el CELESTE de Booster.
 *
 * (25/08/2026) Era el cian `#22D3EE`. El celeste entró como conmemoración del 25 de Agosto
 * en el CRM de Imperium, se vio un día entero puertas adentro y afuera, y se adoptó como el
 * color de Booster. Los valores son los de `BOOSTER_PALETTE` —la misma escala que ve el
 * CRM—: que la web y el producto usen el mismo color es lo que los hace leerse como la
 * misma cosa.
 *
 * ⛔ NO se importan de `BOOSTER_BASE` aunque sean los mismos valores: acá se capturan a
 * nivel módulo y `EMERALD` ya demostró que así el nombre se despega del color sin aviso.
 * Escritos literales, un grep encuentra todo lo que hay que cambiar la próxima vez.
 *
 * El cian anterior, por si hay que volver: `#22D3EE` · `#0891B2` · `#0E5C6E`
 * (rgba `34,211,238` y el claro `103,232,249`).
 */
export const ACENTO = '#75AADB';
export const ACENTO_MEDIO = '#6695C0';
export const ACENTO_HONDO = '#547A9D';
/**
 * ⛔ EL TEXTO QUE VA ENCIMA DEL ACENTO. Oscuro, no blanco.
 *
 * (25/08/2026) Estaba escrito a mano —`#03222B`, el azul de noche del cian— en OCHO lugares:
 * los botones de la landing, el chip de operación de la ficha anónima, el chat público de
 * Gustavo, el juego. Al pasar la marca al celeste ninguno falló, porque contra el celeste ese
 * azul todavía da 6,73:1 y se lee. Simplemente dejaron de ser el color de la marca sin que
 * nada avisara — la misma familia del acento cian que se quedó en el login.
 *
 * Es el mismo valor que `BOOSTER_PALETTE.brand.onAccent`, elegido midiendo contraste: sobre el
 * celeste el blanco da 2,46:1 (ilegible) y el negro 7,07:1.
 */
export const SOBRE_ACENTO = '#1A1A1A';

/** rgba del acento, para glows y bordes. `acL` es el mismo tono, más claro. */
export const ac  = (a: number) => `rgba(117,170,219,${a})`;
export const acL = (a: number) => `rgba(155,193,229,${a})`;

/**
 * ⚠️ El emerald tiene UN trabajo: marca y acción — botón primario, link, estado activo.
 * Venía pintando además eyebrows, bordes, glows, barras y el gradiente de cada titular, y
 * cuando todo brilla no brilla nada. Para lo demás están los dos acentos de abajo.
 */

/**
 * SEÑAL DE IA: acciones que ejecuta Gustavo, datos vivos, telemetría.
 *
 * ⚠️ Es el MISMO valor que `ACENTO` y así viene desde el 20/08/2026 — dos nombres para un
 * color. Se conservan separados porque dicen cosas distintas (uno es la marca, el otro es
 * "esto lo hace la IA") y porque si algún día se despegan, los 52 usos de `SIGNAL` ya están
 * marcados. Pero mientras valgan lo mismo, TIENEN QUE cambiar juntos: dejar `SIGNAL` en
 * cian con el acento en celeste pone dos azules parientes a pelearse en la misma pantalla.
 *
 * ⛔ El cian del mapa neuronal DENTRO del CRM (`gustavoMonitor/brain`) es otra cosa y no se
 * toca: ahí el color codifica qué modelo resuelve el turno, no la marca.
 */
export const SIGNAL = '#75AADB';
export const SIGNAL_DEEP = '#6695C0';
/** CANAL HUMANO. El violeta del Agente Virtual: WhatsApp, voz, conversación con el cliente. */
export const HUMAN = '#8B6BF0';

/**
 * ── Escala fría ────────────────────────────────────────────────────────────
 *
 * El texto venía de `CRM_PALETTE`, que es la paleta DORADA de Imperium: crema #F0EDE6 y
 * gris marrón #9C9487 sobre un fondo azulado con acento verde. Esa mezcla de temperaturas
 * es lo que hacía ver toda la landing polvorienta. Ahora el mismo hue del fondo recorre el
 * texto, la superficie y el borde.
 */
export const INK = '#04060A';                      // base
export const INK_2 = '#070B11';                    // banda alterna
export const TXT = '#E6EDF2';                      // frío, no crema
export const TXT_DIM = '#8FA0AC';                  // frío, no marrón
export const TXT_MUTE = '#5E6E7A';                 // el tercer escalón, para etiquetas
export const PAPER = P.bg.paper;                   // #141414 — legado, ver SURFACE

/**
 * SUPERFICIE. `PAPER` opaco cortaba la nebulosa en seco y las tarjetas se leían como
 * parches grises. Estas tres son vidrio teñido del propio fondo: el fondo se sigue viendo
 * debajo, que es de dónde sale la profundidad.
 */
/**
 * SUPERFICIE — vidrio AZUL, no verde.
 *
 * Probamos teñirla del verde de marca y el resultado fue el contrario del buscado: con el
 * fondo verde, las tarjetas verdes, los bordes verdes y el cerebro verde, el verde dejó de
 * ser un acento y pasó a ser el ambiente. Cuando todo es del color de la marca, la marca
 * no se ve.
 *
 * La regla nueva: el VERDE es de la MARCA (el logotipo y el mapa neuronal, que es el
 * producto). Todo lo demás —fondo, superficies, bordes— es frío y azul, y por eso el verde
 * del cerebro y el cian de la acción se recortan encima. (20/08/2026)
 */
export const SURFACE = 'rgba(12,26,40,0.66)';
export const SURFACE_HI = 'rgba(17,34,52,0.82)';
export const LINE = 'rgba(120,170,205,0.16)';

// rgba helpers para glows/bordes
export const em = (a: number) => `rgba(5,150,105,${a})`;
export const emL = (a: number) => `rgba(52,211,153,${a})`;
export const sig = (a: number) => `rgba(117,170,219,${a})`;   // = ac() — SIGNAL vale lo mismo que ACENTO
/** El claro de la señal. Reemplaza a `emL` donde el verde era del acento y no un estado. */
export const sigL = (a: number) => `rgba(169,203,234,${a})`;
/**
 * El azul del ambiente: es la nebulosa, no un acento. Nunca pinta un elemento.
 *
 * Era `rgba(31,86,168)` —un azul de verdad— y sobre el fondo se leía como una mancha
 * celeste en la esquina. Bajado a un azul de noche muy oscuro: aporta profundidad y
 * temperatura sin que se note que "hay azul ahí". (20/08/2026)
 */
export const azul = (a: number) => `rgba(18,42,74,${a})`;
export const hum = (a: number) => `rgba(139,107,240,${a})`;

/**
 * LA NEBULOSA — el fondo del que hereda cada sección.
 *
 * Tres capas de radial en vez de un negro plano: verde de marca, cian de señal y un
 * violeta apenas perceptible. Es lo que hace que el mapa neuronal del hero se FUNDA con la
 * página en vez de recortarse contra el fondo. Cada sección elige dónde poner los focos.
 */
/**
 * EL FONDO DE LA PÁGINA — uno solo, continuo, detrás de todo.
 *
 * Antes cada sección traía su propia nebulosa y su borde: al scrollear se veía la costura
 * —una línea y un cambio de tono— marcando dónde empezaba y terminaba cada una, como si
 * fueran páginas distintas pegadas. Ahora va una sola capa FIJA detrás de la home entera y
 * las secciones son transparentes: el fondo no se mueve al scrollear y la página se lee
 * como una sola pieza. (20/08/2026)
 *
 * Los focos van a los COSTADOS, altos y bajos, para que el centro quede limpio y el
 * contenido siempre se lea sobre el tono más oscuro.
 */
export function fondoDeLaHome() {
  return [
    `radial-gradient(1200px 900px at -8% 8%, ${azul(0.55)}, transparent 62%)`,
    `radial-gradient(1100px 800px at 108% 26%, ${sig(0.07)}, transparent 60%)`,
    `radial-gradient(1000px 900px at -6% 62%, ${hum(0.06)}, transparent 62%)`,
    `radial-gradient(1200px 900px at 106% 88%, ${azul(0.45)}, transparent 62%)`,
    INK,
  ].join(', ');
}

/**
 * El GRANO, una sola vez y para toda la página.
 *
 * Iba por sección con `mixBlendMode: overlay` sobre un `inset: 0`, así que donde terminaba
 * una sección el grano se cortaba en seco y dejaba un escalón recto — la "división" que se
 * veía entre el hero y lo que sigue, aun sin bordes. Sobre la capa fija es continuo y no
 * hay dónde cortarse. (20/08/2026)
 */
/**
 * La SEDA del fondo: una textura que ondula muy despacio, detrás de todo.
 *
 * El fondo de las páginas públicas era un degradé quieto —correcto y sin vida—. Esto le
 * agrega relieve sin agregar un motivo: al 2% no se ve, se siente. Se probó al 26% (se leía
 * como una tela: bonita, pero algo con lo que el texto y el cerebro del hero tenían que
 * competir) y al 5%.
 *
 * Va FIJA y en el mismo plano que el degradé, así no se corta entre secciones — el mismo
 * motivo por el que el fondo y el grano ya eran capas continuas.
 *
 * ⚠️ Es un canvas WebGL. En la home convive con el del cerebro; conviene medirlo en un
 * equipo flojo antes de darlo por bueno. Va con `lazy` para no retrasar el primer pintado.
 *
 * De React Bits (MIT + Commons Clause), variante TS + CSS plano — sin Tailwind, que con MUI
 * no va. Usa el `three` que ya tenemos: no suma dependencia.
 */
const SedaLazy = lazy(() => import('../lab/fondos/Silk'));

export function FondoDeSeda({ opacidad = 0.02 }: { opacidad?: number }) {
  return (
    <Box aria-hidden sx={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: opacidad }}>
      <Suspense fallback={null}>
        <SedaLazy color={SIGNAL_DEEP} speed={2.4} scale={1.4} noiseIntensity={1.1} rotation={0.12} />
      </Suspense>
    </Box>
  );
}

export const GRANO_CSS = {
  position: 'fixed' as const, inset: 0, pointerEvents: 'none' as const, zIndex: 0,
  opacity: 0.045, mixBlendMode: 'overlay' as const,
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
};

/**
 * EL AURA de una sección: un resplandor propio que NO se corta.
 *
 * El problema de hacerlo directo: cualquier capa con la caja de la sección (`inset: 0`) se
 * recorta donde la sección termina, y si el foco está cerca de un borde —arriba al 6%, o
 * abajo al 90%— ahí el color está a plena intensidad justo donde el navegador corta. Eso
 * dibuja la línea recta que separaba una sección de la otra aun sin bordes ni fondos.
 *
 * La solución es la MÁSCARA vertical: la capa se desvanece sola en el 15% de arriba y de
 * abajo, así llega al borde ya en cero y no hay nada que cortar. Con eso el aura vuelve
 * —sin ella la página quedaba plana— y las secciones se siguen fundiendo entre sí.
 * (20/08/2026)
 *
 * @param donde  posición del foco dentro de la caja ('20% 30%')
 * @param color  función rgba del acento: `sig`, `azul` o `hum`
 */
export function resplandor(donde: string, color: (a: number) => string, fuerza = 0.16) {
  const desvanecer = 'linear-gradient(180deg, transparent 0%, #000 15%, #000 85%, transparent 100%)';
  return {
    position: 'absolute' as const, inset: 0, pointerEvents: 'none' as const, zIndex: 0,
    background: `radial-gradient(1100px 820px at ${donde}, ${color(fuerza)}, transparent 68%)`,
    WebkitMaskImage: desvanecer,
    maskImage: desvanecer,
  };
}

export function nebulosa(opts: { base?: string; verde?: string; cian?: string; violeta?: string } = {}) {
  const capas = [
    // El foco principal ya no es verde: es un AZUL de noche. El verde ahí competía con el
    // cerebro —que es verde y es lo único que tiene que brillar— y teñía la página entera.
    `radial-gradient(1100px 780px at ${opts.verde ?? '76% 4%'}, ${azul(0.22)}, transparent 62%)`,
    `radial-gradient(820px 620px at ${opts.cian ?? '4% 92%'}, ${sig(0.09)}, transparent 62%)`,
    `radial-gradient(700px 560px at ${opts.violeta ?? '46% 58%'}, ${hum(0.07)}, transparent 66%)`,
  ];
  return `${capas.join(', ')}, ${opts.base ?? INK}`;
}

/**
 * Un barrio en la esquina — casas y torres, para la sección de módulos del producto.
 *
 * (25/08/2026, pedido del user) Es DISTINTO de `HorizonteCiudad`: aquél es un skyline ancho
 * anclado al pie de la portada, y éste es un grupo compacto que vive en una esquina de la
 * sección, junto al resplandor azul. Se integra con ese degradado en vez de pelearle: se
 * difumina alejándose de la esquina, que es de donde viene la luz.
 *
 * ⛔ El dibujo y el resplandor van SIEMPRE en la misma esquina; si se mueve uno solo, el
 * barrio queda sin luz y la luz sin nada que iluminar. Hoy vive ARRIBA A LA DERECHA de
 * "Mirá cómo se ve por dentro", que ya tenía su resplandor ahí (`86% 20%`). Estuvo en la
 * sección de módulos hasta el 04/09/2026 — el user lo probó en las cuatro esquinas y lo
 * terminó mandando a la sección siguiente.
 *
 * Mezcla casas con techo a dos aguas y torres planas a propósito. Solo torres es la ciudad de
 * la portada otra vez; solo casas, un barrio de las afueras. Las dos juntas son de lo que
 * habla la sección: todo el negocio inmobiliario, no un tipo de propiedad.
 */
export function BarrioEnLaEsquina() {
  // El dibujo se apaga hacia abajo y hacia la derecha para fundirse con el resto de la sección.
  /**
   * ⛔ LOS NÚMEROS DE ACÁ SON LOS QUE LO HACEN VISIBLE, Y LA PRIMERA VERSIÓN NO LO ERA.
   *
   * Iba con `opacity: 0.5` sobre trazos de alpha 0.05: 0,04 efectivo sobre un fondo casi negro,
   * o sea nada. Se veía una mancha detrás del título y punto. El contenedor ya no baja la
   * opacidad —eso lo gradúa el alpha de cada fila— y la máscara apaga menos.
   */
  const desvanecer = 'radial-gradient(135% 135% at 100% 0%, #000 45%, rgba(0,0,0,0.7) 72%, transparent 100%)';
  return (
    <Box aria-hidden sx={{
      /**
       * ⛔ `right: calc(-50vw + 50%)` LLEGA AL BORDE DE LA PANTALLA, no al de la sección.
       *
       * `Section` mete su contenido en un contenedor de 1200px centrado (`mx: auto`), así que
       * un `right` negativo común arranca en el borde de ESE contenedor: en una pantalla de
       * 1600px el dibujo quedaba a 200px del borde real y se leía flotando en el medio.
       */
      position: 'absolute', top: { xs: -50, md: -100 }, right: 'calc(-50vw + 50%)',
      // A 560×320 el barrio se leía como un detalle en la esquina; al triple tapaba la
      // sección entera. Este es el punto medio: se lee como fondo sin competir con las
      // tarjetas. (25/08/2026)
      width: { xs: 510, md: 840 }, height: { xs: 285, md: 480 },
      pointerEvents: 'none', zIndex: 0, opacity: 0.5,
      WebkitMaskImage: desvanecer, maskImage: desvanecer,
    }}>
      <Box component="svg" viewBox="0 0 424 200" preserveAspectRatio="xMaxYMin meet"
        sx={{ width: '100%', height: '100%', display: 'block' }}>
        {/* Fila del fondo: más baja y más apagada — da la profundidad del barrio. */}
        <path fill={alpha(TXT, 0.045)}
          d="M6 200 V156 L43 130 L80 156 V200 Z M90 200 V114 H142 V200 Z M150 200 V148 L194 118 L238 148 V200 Z M246 200 V130 H290 V200 Z M298 200 V162 L331 138 L364 162 V200 Z" />
        {/* Fila de adelante. */}
        <path fill={alpha(TXT, 0.075)}
          d="M0 200 V136 L48 98 L96 136 V200 Z M104 200 V88 H164 V200 Z M172 200 V128 L214 88 L256 128 V200 Z M264 200 V142 L315 108 L366 142 V200 Z M374 200 V106 H424 V200 Z" />
      </Box>
    </Box>
  );
}

/**
 * EL HORIZONTE — el guiño inmobiliario del hero.
 *
 * Una silueta de ciudad en la base, tan tenue que se nota recién cuando uno la busca. La
 * página habla de IA y el mapa neuronal se lleva toda la atención; esto no compite con él
 * —está abajo, él está arriba a la derecha— sino que ancla de qué NEGOCIO estamos
 * hablando. Sin esto el hero podía ser el de cualquier producto de software.
 *
 * Dibujada a mano y no con una imagen: pesa 1 kB, escala a cualquier ancho y toma el color
 * del texto, así que acompaña el tema sin un asset que mantener.
 *
 * La máscara vertical es la misma lección que el resplandor: la silueta se desvanece antes
 * de llegar al borde del hero, así no deja una línea donde la sección termina. (20/08/2026)
 */
export function HorizonteCiudad() {
  const desvanecer = 'linear-gradient(180deg, transparent 0%, #000 45%, #000 86%, transparent 100%)';
  return (
    <Box aria-hidden sx={{
      // Se pasa un 3% de cada lado a propósito: los extremos del dibujo son manzanas
      // bajas, y terminando justo en el borde la ciudad parecía apagarse ahí. Sobrando,
      // el corte queda fuera de la pantalla y se lee continua. El DOBLE de alta que
      // antes: a 210px era un zócalo, no un horizonte. (20/08/2026)
      position: 'absolute', left: '-3%', right: '-3%', bottom: 0, height: { xs: 240, md: 420 },
      pointerEvents: 'none', zIndex: 0, opacity: 0.5,
      WebkitMaskImage: desvanecer, maskImage: desvanecer,
    }}>
      <Box component="svg" viewBox="0 0 1600 210" preserveAspectRatio="none"
        sx={{ width: '100%', height: '100%', display: 'block' }}>
        {/* Fila del fondo: más baja y más apagada — da la profundidad de la ciudad. */}
        <path
          fill={alpha(TXT, 0.045)}
          d="M0 210V120h60v8h48v22h74v-40h56v40h92v-30h64v30h86v-52h58v52h96v-24h62v24h84v-44h60v44h92v-30h66v30h88v-40h58v40h96v-18h62v18h94v-34h58v2h72v92z"
        />
        {/* Fila de adelante: torres más altas y un par de casas bajas a la derecha. */}
        <path
          fill={alpha(TXT, 0.075)}
          d="M0 210v-70h40v-20h52v52h40v-74h58v74h44v-30h50v30h56v-88h56v88h48v-46h54v46h62v-62h56v62h50v-28h48v28h60v-70h58v70h46v-40h52v40h58v-24h46v24h56v-56h56v56h44v-34h48v34h60v-20l30-14 30 14v20h58v-26l28-13 28 13v26v-32h64v70z"
        />
        {/* Dos ventanas encendidas: el detalle que hace que se lea como ciudad y no como
            un patrón. Van en el acento, que es lo único con color acá abajo. */}
        <rect x="640" y="140" width="5" height="7" fill={alpha(SIGNAL, 0.5)} />
        <rect x="1188" y="120" width="5" height="7" fill={alpha(SIGNAL, 0.35)} />
      </Box>

      {/* Los carteles van en HTML y no adentro del SVG: el dibujo se estira sin conservar
          proporción (`preserveAspectRatio="none"`), así que cualquier texto ahí adentro
          saldría deformado. Acá encima quedan nítidos y se ubican por porcentaje. */}
      {CARTELES.map((c) => (
        <Box key={c.texto + c.left} sx={{ position: 'absolute', left: c.left, bottom: c.bottom, display: { xs: 'none', md: 'block' } }}>
          <Box sx={{
            fontFamily: FONT_TECH, fontSize: 8.5, letterSpacing: '0.14em', lineHeight: 1,
            px: 0.9, py: 0.6, borderRadius: '2px', whiteSpace: 'nowrap',
            color: c.destacado ? alpha(SIGNAL, 0.75) : alpha(TXT, 0.3),
            border: `1px solid ${c.destacado ? alpha(SIGNAL, 0.4) : alpha(TXT, 0.16)}`,
            background: c.destacado ? alpha(SIGNAL, 0.07) : alpha(TXT, 0.03),
          }}>
            {c.texto}
          </Box>
          {/* el parante que lo planta en la azotea */}
          <Box sx={{ width: '1px', height: c.poste, mx: 'auto', background: alpha(TXT, 0.16) }} />
        </Box>
      ))}
    </Box>
  );
}

/**
 * Los carteles de la ciudad: el guiño que termina de decir de qué negocio hablamos.
 *
 * "Se alquila" y no "se renta" porque es lo que dice el producto en todos lados —el portal
 * ofrece "Comprar / Alquilar"— y porque es como se dice en el Río de la Plata. (20/08/2026)
 */
const CARTELES = [
  { left: '13%', bottom: 128, poste: 26, texto: 'SE VENDE',   destacado: false },
  /**
   * Plantado en la torre alta del medio (x≈774-832 del dibujo, la que sube a 102).
   *
   * Al 39% caía detrás de "De captación a publicación" y los textos se pisaban; al 54% se
   * metía abajo del cerebro. Acá tiene una azotea de verdad donde pararse y queda entre
   * los dos. (20/08/2026)
   */
  { left: '47%', bottom: 192, poste: 34, texto: 'SE ALQUILA', destacado: true },
  { left: '61%', bottom: 112, poste: 22, texto: 'SE VENDE',   destacado: false },
  { left: '86%', bottom: 150, poste: 30, texto: 'SE ALQUILA', destacado: false },
];

// ── Reveal en scroll (una sola vez) ─────────────────────────────────────────
export function Reveal({
  children, delay = 0, y = 26, style,
}: { children: ReactNode; delay?: number; y?: number; style?: CSSProperties }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-70px' });
  return (
    <motion.div
      ref={ref}
      style={style}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : undefined}
      transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

/**
 * Etiqueta "eyebrow": monoespaciada, tracked, con su línea.
 *
 * `color` deja elegir el acento de la sección — verde donde se habla de la plataforma,
 * cian donde habla la IA. Centrada lleva línea de los dos lados, si no la de la izquierda
 * queda huérfana contra el borde.
 */
export function Eyebrow({ children, center = false, color = SIGNAL }: {
  children: ReactNode; center?: boolean; color?: string;
}) {
  const linea = (dir: number) => (
    <Box component="span" aria-hidden sx={{
      width: 26, height: '1px',
      background: `linear-gradient(${dir}deg, transparent, ${color})`,
    }} />
  );
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 1.5,
        fontFamily: FONT_TECH, fontSize: 10.5, fontWeight: 500,
        letterSpacing: '0.26em', textTransform: 'uppercase',
        color,
        justifyContent: center ? 'center' : 'flex-start',
      }}
    >
      {linea(90)}
      {children}
      {center && linea(270)}
    </Box>
  );
}

// ── Orbe "Gustavo": núcleo esmeralda con aura que respira ───────────────────
export function GustavoOrb({ size = 280 }: { size?: number }) {
  return (
    <Box sx={{ position: 'relative', width: size, height: size, mx: 'auto' }}>
      {/* halo exterior pulsante */}
      <motion.div
        animate={{ scale: [1, 1.12, 1], opacity: [0.5, 0.85, 0.5] }}
        transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          background: `radial-gradient(circle, ${em(0.55)} 0%, ${em(0.12)} 45%, transparent 70%)`,
          filter: 'blur(8px)',
        }}
      />
      {/* anillos orbitando */}
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          animate={{ rotate: i % 2 === 0 ? 360 : -360 }}
          transition={{ duration: 26 + i * 9, repeat: Infinity, ease: 'linear' }}
          style={{
            position: 'absolute', inset: `${8 + i * 11}%`, borderRadius: '50%',
            border: `1px solid ${emL(0.18 - i * 0.04)}`,
            borderTopColor: emL(0.5 - i * 0.12),
          }}
        />
      ))}
      {/* núcleo */}
      <Box
        sx={{
          position: 'absolute', inset: '30%', borderRadius: '50%',
          background: `radial-gradient(circle at 35% 30%, ${EMERALD_LIGHT} 0%, ${EMERALD} 45%, ${EMERALD_DEEP} 100%)`,
          boxShadow: `0 0 60px ${em(0.7)}, inset 0 0 40px ${emL(0.5)}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <motion.div
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            fontFamily: FONT_TECH, fontWeight: 800, color: '#fff',
            fontSize: size * 0.14, letterSpacing: '0.05em',
            textShadow: `0 0 18px ${emL(0.9)}`,
          }}
        >
          G
        </motion.div>
      </Box>
    </Box>
  );
}

// ── Capa de "grain" sutil para dar textura al fondo ─────────────────────────
/**
 * @deprecated Por sección NO: `mixBlendMode` sobre un `inset: 0` se corta donde termina la
 * caja y deja un escalón recto entre secciones. La home usa `GRANO_CSS`, una sola capa fija
 * para toda la página. Queda por si alguna pantalla suelta lo necesita acotado.
 */
export function Grain() {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.04, mixBlendMode: 'overlay',
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
      }}
    />
  );
}

// ── Shell de sección (padding + id para anclas) ─────────────────────────────
export function Section({
  id, children, sx,
}: { id?: string; children: ReactNode; sx?: object }) {
  return (
    <Box
      component="section"
      id={id}
      // Menos aire entre secciones: a 16 la página se sentía vacía entre bloque y bloque
      // y obligaba a scrollear de más. (04/09/2026, pedido del user)
      sx={{ position: 'relative', px: { xs: 3, md: 6 }, py: { xs: 7, md: 11 }, ...sx }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto', position: 'relative' }}>{children}</Box>
    </Box>
  );
}
